<template lang="pug">
.media-upload(:class="`${error ? 'error' : ''}`")
  portal(to="modals")
    Modal(:show="unsplashOpen", @close="closeModal()", v-if="unsplash" :title="$t('poster.find_on_unsplash')" id="unsplashModal").state
      template(v-slot:subheader)
        FormInput(type="text" :outline="true" :placeholder="$t('upload.search_placeholder')", v-model="unsplashSearch", :value="unsplashSearch" class="f-w")
      template(v-if="unsplashState === '' && unsplashSearch === ''")
        .state
          i.mdi.mdi-magnify
          p.state-text {{ $t('upload.search_unsplash')}}
      template(v-else-if="unsplashState === 'searching'")
        .state
          i.mdi.mdi-loading.mdi-spin
          p.state-text {{ $t('upload.searching_unsplash')}}..
      template(v-else-if="unsplashResult.result.length === 0")
        .state
          i.mdi.mdi-magnify
          p.state-text {{ $t('upload.empty_search_result')}}
      template(v-else)
        .unsplash-result
          .unsplash-item(v-for="(image, index) in unsplashResult.result")
            .unsplash-image(@click="selectUnsplashImage(image, index)")
              img(:src="image.urls.thumb")
              .adding(v-if="unsplashDownloading && unsplashDownloadingIndex === index"): i.mdi.mdi-loading.mdi-spin.icon
            .unsplash-image-info
              p {{ $t('fileupload.photo_by') }}&nbsp;
                a(:href="`https://unsplash.com/@${image.user.username}?utm_source=streamify&utm_medium=referral`", target="_blank") {{ image.user.name }}&nbsp;
        .infinity: template(v-if="unsplashInfinityLoading"): i.mdi.mdi-loading.mdi-spin.icon
  CardMinimal(:key="imagesKey")
    .p-a-3
      .upload-area(v-if="images.length === 0", :class="`${highlighted ? 'highlighted' : ''} ${chessboard ? 'chessboard' : ''}`")
        .upload-text
          template(v-if="state === ''")
            i.mdi(:class="`mdi-${multiple ? 'plus-circle-multiple-outline' : 'plus-circle-outline'}`")
            | {{ customText || $t('products.attach_new_image') }}...
          template(v-else-if="state === 'uploading'")
            i.mdi.mdi-loading.mdi-spin.loading
        input(type="file", :multiple="multiple", ref="upload-area-input", @change="uploadImages($event.target.files)", @dragenter="highlight(true)", @dragleave="highlight(false)")
      .image-gallery(:class="`${chessboard ? 'chessboard' : ''}`")
        .images(v-for="(image, index) in images", :key="index", :style="`background-image: url('${image.value.substring(0, 4) === 'http' || image.value.substring(0, 4) === 'data' ? image.value : `https://${fqdn.split('.')[0]}.streamify.io/images/${size}/${image.value}`}')`", :class="`${galleryIndex === index ? 'active' : ''}`")
        .image-buttons
          IconButton(:text="$t('upload.search_from_unsplash')", @clicked="state === 'uploading' ? null : unsplashOpen = true", v-if="unsplash && images.length === 0").blue
          IconButton(:icon="images[0].deleted ? 'undo' : 'trash-can-outline'", @clicked="images[0].deleted ? unremoveImage(0) : removeImage(0)", :class="`${images[0].deleted ? 'blue' : 'red'}`", v-if="images.length === 1" :disabled="disabled").m-l-1
        .dots(v-if="multiple && images.length > 1")
          .dot(v-for="(image, index) in images", :key="`dot-${index}`", @click="galleryIndex = index", :class="`${galleryIndex === index ? 'active' : ''}`")
      .image-list(v-if="multiple && images.length > 1")
        .image(v-for="(image, index) in images", :key="index")
          img(:src="`https://${fqdn.split('.')[0]}.streamify.io/images/64x64/${image.value}`")
          span {{ index === 0 ? 'Huvudbild' : 'Extrabild' }}
          .icon-button-group.ml-a
            IconButton(icon="menu-down", :class="`${index === images.length - 1 ? 'gray' : 'blue'}`", @clicked="index === images.length - 1 ? null : move(false, index)")
            IconButton(icon="menu-up", :class="`${index === 0 ? 'gray' : 'blue'}`", @clicked="index === 0 ? null : move(true, index)")
          IconButton(:icon="image.deleted ? 'undo' : 'trash-can-outline'", :class="`${image.deleted ? 'blue' : 'red'}`", @clicked="image.deleted ? unremoveImage(index) : removeImage(index)" :disabled="disabled")
      .upload-input
        input(type="file", multiple, ref="upload-input", @change="uploadImages($event.target.files)")
    p(class="image-error" v-if="error") {{ error }}
    .p-a-4.d-f.ai-c.jc-c(slot="footerButton", v-if="multiple && images.length > 0", @click="state === 'uploading' ? null : $refs['upload-input'].click()")
      template(v-if="state === 'uploading'")
        i.mdi.mdi-loading.mdi-spin
      template(v-else)
        i.mdi.mdi-plus-circle
        span {{ $t('products.add_image') }}
</template>

<script>

export default {
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    size: {
      type: String,
      default: '300x300',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    chessboard: {
      type: Boolean,
      default: false,
    },
    customText: {
      type: String,
      default: '',
    },
    overwrite: {
      type: Boolean,
      default: false,
    },
    unsplash: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    legacy: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    closeModal() {
      this.unsplashOpen = false;
    },
    /**
     * Upload image(s)
     * @param {Array} images
     */
    async uploadImages(images) {
      if (images.length === 0) {
        return;
      }

      if (this.legacy) {
        const formData = new FormData();
        const reader = new FileReader();

        reader.onload = (e) => {
          this.fileupload = e.target.result;
          formData.append('file', images[0]);
          this.$emit('images', formData);

          if (this.overwrite && this.images.length > 0) {
            this.images[0].value = e.target.result;
          } else {
            this.images.push({ value: e.target.result });
          }
        };
        reader.readAsDataURL(images[0]);

        return;
      }

      const {
        multiple,
        overwrite,
        data: {
          edit,
        },
      } = this;

      this.state = 'uploading';

      // eslint-disable-next-line
      for (const image of images) {
        if (['image/jpeg', 'image/png'].indexOf(image.type) >= 0) {
          const imageData = new FormData();
          imageData.append('file', image);
          // eslint-disable-next-line
          const result = await this.$store.dispatch('Upload/uploadImages', imageData);
          if (result) {
            if (overwrite && this.images.length > 0) {
              this.images[0].value = result.filename;
            } else {
              this.images.push({ value: result.filename });
            }
          }
        }
      }

      if (this.$refs['upload-area-input']) {
        this.$refs['upload-area-input'].value = '';
      }

      if (this.$refs['upload-input']) {
        this.$refs['upload-input'].value = '';
      }

      if (!edit) {
        this.$emit('images', this.images);
      }

      this.state = '';
    },
    /**
     * Update images in their respective given store-type
     */
    async update() {
      const {
        images,
        data: {
          id,
          action,
        },
      } = this;

      await this.$store.dispatch(action, {
        id,
        images: images.filter(image => !image.deleted).map(image => image.value),
      });

      return false;
    },
    async searchUnsplash() {
      const { data } = await ovpFetch(`https://api.unsplash.com/search/photos?client_id=${this.unsplashKey}&page=${this.unsplashPage}&per_page=12&query=${this.unsplashSearch}`, {
        noAuth: true,
        noExtraHeaders: true,
        // Remove for now?
        // headers: {
        //   Authorization: `Client-ID ${this.unsplashKey}`,
        // },
      });

      this.unsplashResult.total = data.total;
      this.unsplashResult.total_pages = data.total_pages;
      this.unsplashResult.result = this.unsplashResult.result.concat(data.results);
      this.unsplashState = '';

      this.$nextTick(() => {
        this.unsplashInfinityLoading = false;
        if (this.unsplashPage < data.total_pages) {
          document.querySelector('#unsplashModal .modal-content').addEventListener('scroll', this.unsplashScroll);
        }
      });
    },
    unsplashScroll() {
      const { unsplashInfinityLoading } = this;
      if (unsplashInfinityLoading) {
        return;
      }

      const el = document.querySelector('#unsplashModal .modal-content');

      if (el.scrollHeight - el.scrollTop <= el.offsetHeight + 20) {
        document.querySelector('#unsplashModal .modal-content').removeEventListener('scroll', this.unsplashScroll);
        this.unsplashInfinityLoading = true;
        this.unsplashPage += 1;
        this.searchUnsplash();
      }
    },
    /**
     * Remove image (metaphoricly speaking)
     * @param {Index} index
     */
    removeImage(index) {
      const {
        multiple,
        overwrite,
        data: {
          edit,
        },
      } = this;

      if (typeof this.images[index].deleted === 'undefined' || overwrite) {
        this.images.splice(index, 1);
      } else {
        this.images[index].deleted = true;
      }

      if (!edit) {
        this.$emit('images', multiple ? this.images : this.legacy ? null : []);
      }
    },
    unremoveImage(index) {
      this.images[index].deleted = false;
    },
    /**
     * Move image in array
     * @param {Boolean} up
     * @param {Number} index
     */
    move(up, index) {
      const [image] = this.images.splice(index, 1);
      this.images.splice(up ? index - 1 : index + 1, 0, image);
    },
    /**
     * Set highlighted
     * @param {Boolean} bool
     */
    highlight(bool) {
      this.highlighted = bool;
    },
    /**
     * Just return true
     */
    validate() {
      return true;
    },
    reset() {
      if (this.data.edit) {
        this.images = this.data.images.map(i => ({ value: i, deleted: false }));
        this.imagesKey += 1;
      }
    },
    async selectUnsplashImage(image, index) {
      if (this.unsplashDownloading) {
        return;
      }

      this.unsplashDownloading = true;
      this.unsplashDownloadingIndex = index;

      const { data } = await ovpFetch(`${image.links.download_location}&client_id=${this.unsplashKey}`, {
        noAuth: true,
        noExtraHeaders: true,
        // headers: {
        //  Authorization: `Client-ID ${this.unsplashKey}`,
        // },
      }).catch(() => false);

      if (!data) {
        this.unsplashDownloading = false;
        this.unsplashDownloadingIndex = null;
        return;
      }

      const { data: imageResponse } = await ovpFetch(`${data.url}&client_id=${this.unsplashKey}`, {
        responseType: 'blob',
        noAuth: true,
        noExtraHeaders: true,
        // headers: {
        //  Authorization: `Client-ID ${this.unsplash.key}`,
        // },
      }).catch(() => false);
      if (!imageResponse) {
        this.unsplashDownloading = false;
        this.unsplashDownloadingIndex = null;
        return;
      }
      await this.uploadImages([imageResponse]);

      this.unsplashDownloading = false;
      this.unsplashDownloadingIndex = null;
      this.unsplashOpen = false;
    },
  },
  watch: {
    data: {
      immediate: true,
      handler({ edit, images, force = false }) {
        if (this.first) {
          return;
        }
        this.first = true;

        if (!edit && !force) {
          return;
        }

        if (images[0] !== undefined) {
          this.images = images.map(image => ({ value: image.value || image, deleted: false }));
        }
      },
    },
    unsplashSearch() {
      clearTimeout(this.unsplashTimer);
      this.unsplashState = 'searching';
      this.unsplashPage = 1;
      this.unsplashResult.result = [];
      this.unsplashTimer = setTimeout(() => {
        this.searchUnsplash();
      }, 500);
    },
  },
  data() {
    return {
      first: false,
      highlighted: false,
      galleryIndex: 0,
      state: '',
      images: [],
      imagesKey: 0,
      unsplashOpen: false,
      unsplashPage: 1,
      unsplashSearch: '',
      unsplashState: '',
      unsplashTimer: null,
      unsplashDownloading: false,
      unsplashDownloadingIndex: null,
      unsplashKey: process.env.VUE_APP_UNSPLASHKEY,
      unsplashInfinityLoading: false,
      unsplashResult: {
        total: 0,
        total_pages: 0,
        result: [],
      },
    };
  },
};
</script>

<style lang="scss" scoped>
.unsplash-result {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  width: 100%;
  padding: 10px;

  .unsplash-item {
    background-color: white;
    border-radius: 5px;
    margin: .5rem;
    width: calc(33.333% - 1rem);
    .unsplash-image {
      overflow: hidden;
      position: relative;

      .adding {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        color: black;
        background-color: white;
        border-radius: 100%;
        width: 2rem;
        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;

        i {
          font-size: 1rem;
        }
      }

      img {
        border-radius: 5px 5px 0 0;
        transition: transform .5s;
        width: 100%;
      }

      .choose-image {
        border-radius: 5px;
        position: absolute;
        left: 50%;
        top: 50%;
        font-size: .65rem;
        transition: opacity .5s;
        transform: translate(-50%, -50%);
        opacity: 0;
        background-color: rgba(#FAFBFC, .8);
        padding: .15rem .25rem;
        box-shadow: 0 6px 12px 0 rgba(0,0,0,0.04), 0 2px 4px 0 rgba(0,0,0,0.04);
      }

      &:hover {
        cursor: pointer;

        .choose-image {
          opacity: 1;
        }

        img {
          transform: scale(1.2);
        }
      }
    }

    .unsplash-image-info {
      padding: .5rem;

      p {
        font-size: .8rem;
        line-height: 1rem;
        a {
          font-weight: 700;
        }
      }
    }
  }
}

.media-upload.error {
  .upload-area {
    border-color: var(--color-cta-red);
  }
}

.upload-area {
  border: 1px dashed #979797;
  position: relative;
  transition: border-color .3s;

  &.chessboard {
    background-image: linear-gradient(45deg, #EAEAEA 25%, transparent 25%), linear-gradient(-45deg, #EAEAEA 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #EAEAEA 75%), linear-gradient(-45deg, transparent 75%, #EAEAEA 75%);
    background-size: 24px 24px;
    background-position: 0 0, 0 12px, 12px -12px, -12px 0px;
  }

  &.highlighted {
    border-color: #0062FF;
  }

  &:after {
    content: "";
    display: block;
    padding-bottom: 100%;
  }

  .upload-text {
    align-items: center;
    color: #0062FF;
    display: flex;
    justify-content: center;
    height: 100%;
    padding: 15px;
    position: absolute;
    word-break: break-word;
    width: 100%;
    pointer-events: none;

    i {
      margin-right: 10px;
      font-size: 1.5rem;

      &.loading {
        font-size: 2.5rem;
      }
    }
  }

  input[type="file"] {
    height: 100%;
    position: absolute;
    width: 100%;
    opacity: 0;

    &:hover {
      cursor: pointer;
    }
  }
}

.image-gallery {
  position: relative;
  &.chessboard {
    background-image: linear-gradient(45deg, #EAEAEA 25%, transparent 25%), linear-gradient(-45deg, #EAEAEA 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #EAEAEA 75%), linear-gradient(-45deg, transparent 75%, #EAEAEA 75%);
    background-size: 24px 24px;
    background-position: 0 0, 0 12px, 12px -12px, -12px 0px;
  }

  .image-buttons {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    position: absolute;
    bottom: 5px;
    right: 10px;
    & > * {
      margin-bottom: 5px;
    }
  }
  .images {
    border-radius: 4px;
    display: none;
    overflow: hidden;
    padding-top: 100%;
    width: 100%;
    position: relative;
    background-position: 50% 50%;
    background-repeat: no-repeat;
    // background-size: cover;

    &.active {
      display: block;
    }
  }

  .dots {
    align-items: center;
    display: flex;
    justify-content: center;
    margin: 12px 0;

    .dot {
      border: 2px solid #ADAEAF;
      border-radius: 100%;
      width: 10px;
      height: 10px;
      margin-right: 12px;

      &:last-child {
        margin-right: 0;
      }
      &:hover {
        cursor: pointer;
      }
      &.active {
        background-color: black;
        border: 1px solid black;
        height: 12px;
        width: 12px;
        margin-right: 10px;

        &:last-child {
          margin-right: 0;
          margin-left: -2px;
        }
      }
    }
  }
}

.image-list {
  .image {
    align-items: center;
    display: flex;
    margin-bottom: 4px;

    img {
      margin-right: 12px;
      line-height: 0;
    }
  }
}

.upload-input {
  visibility: hidden;
  overflow: hidden;
  height: 0px;
  width: 0px;
}
</style>
